/**
 * The application: session, state, routing, rendering and events.
 *
 * WHY NOT A FRAMEWORK
 *   Nine screens, one data client, and no dependency graph to keep in step with
 *   a monorepo that currently has four runtime packages in total. What the
 *   application needs from a view layer is: a string in, a string out; one
 *   place that decides what a route loads; and a re-render that does not steal
 *   the caret. That is what this file is.
 *
 * TWO THINGS THIS FILE IS CAREFUL ABOUT
 *
 *   1. FOCUS AND SELECTION SURVIVE A RE-RENDER. Filters re-render on every
 *      keystroke, and a naive `innerHTML` assignment moves the caret to the end
 *      of the field and drops the selection. The id and the selection range are
 *      captured before the swap and restored after it, and the scroll positions
 *      of named regions are restored too.
 *
 *   2. AN UNSAVED FIELD IS NEVER LOST TO A NAVIGATION, AND NOT LOST TO A
 *      FAILURE EITHER. A view that owns a dirty field sets `dirty`; the router
 *      asks before it leaves. That covers a deliberate move. It does not cover
 *      the two failures that throw the text away while the tab is still open:
 *      an expired session navigates to `/login` in `load()` below, and a reload
 *      builds a new `App`. Both are recoverable, and `lib/unsaved.ts` keeps the
 *      text per (person, draft) in this tab so the editor can put it back. The
 *      rule that no failure clears a field is enforced by views never writing
 *      into a field they did not receive.
 */

import { type Client, type Failure, type Result, createClient } from './api/client.js';
import * as api from './api/queries.js';
import type { ClientGrantRow, MembershipRow, OrganizationRow } from './api/queries.js';
import { type PeopleDirectory, peopleDirectory } from './lib/format.js';
import { type RawHtml, html, raw } from './lib/html.js';
import { resolveAction } from './lib/actions.js';
import { type Route, matchRoute } from './lib/router.js';
import { renderShell } from './views/shell.js';
import { renderAdmin, loadAdmin } from './views/admin.js';
import { renderConnect, loadConnect } from './views/connect.js';
import { renderAuthorize, loadAuthorize } from './views/consent.js';
import { renderImprovements, loadImprovements } from './views/improvements.js';
import { renderLibrary, loadLibrary } from './views/library.js';
import { renderReview, loadReview } from './views/review.js';
import { renderSkillDetail, loadSkillDetail } from './views/skill.js';
import { renderSkillEditor, loadSkillEditor } from './views/editor.js';
import { renderToolkits, loadToolkits } from './views/toolkits.js';
import { renderGate } from './views/gate.js';
import { registerActions, consumeDialogReturnFocus } from './views/actions.js';
import { readUnsaved } from './lib/unsaved.js';
import { startPublicationPoll, stopPublicationPoll } from './views/publication.js';
import type { EditorData } from './views/editor.js';

const SESSION_KEY = 'aquifer.session.v1';

/**
 * How long the library search waits after the last keystroke before it asks the
 * server anything.
 *
 * MEASURED, 2026-10-01, real Chromium against the running API, signed in as an
 * administrator, typing "reconciliation" then "fasttyping" at two cadences:
 * inter-keystroke gaps were 76-86ms at 70ms between keys and 32-43ms at 25ms.
 * Those are the numbers this has to clear: a burst is a run of keystrokes with
 * gaps under 150ms, which is faster than a person types deliberately and far
 * faster than they type while thinking.
 *
 * 200ms clears the longest gap measured inside a burst by more than 2x, so a
 * burst is always coalesced into one request; and it sits under the ~300ms
 * where a search stops feeling like it is answering you. Below ~150ms it starts
 * splitting a real word into two requests and saves nothing, because the second
 * request is issued on a load that is about to be superseded anyway.
 *
 * The cost of getting this wrong is not one wasted request: `load()` also
 * revalidates the shell, so every keystroke was costing four reads
 * (memberships, drafts, grants, skills) — measured, 58 requests for the 7
 * characters of "payment" before this was in place.
 */
export const SEARCH_DEBOUNCE_MS = 200;
const THEME_KEY = 'aquifer.theme.v1';

export interface Session {
  readonly token: string;
  readonly userId: string;
  readonly orgId: string;
  readonly expiresAt: number | null;
}

/**
 * Read the claims out of a token without trusting them.
 *
 * This is presentation only. `apps/api/src/identity.ts` verifies the signature
 * and the time bounds on every request and the database decides everything
 * else, so a doctored claim here changes what a label says and nothing else.
 */
function decodeSession(token: string): Session | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  let claims: Record<string, unknown>;
  try {
    const segment = parts[1] ?? '';
    claims = JSON.parse(atob(segment.replace(/-/g, '+').replace(/_/g, '/'))) as Record<
      string,
      unknown
    >;
  } catch {
    return null;
  }
  const sub = claims.sub;
  const org = claims.org_id;
  if (typeof sub !== 'string' || typeof org !== 'string') return null;
  const exp = claims.exp;
  return { token, userId: sub, orgId: org, expiresAt: typeof exp === 'number' ? exp * 1000 : null };
}

export type ScreenState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'failed'; readonly failure: Failure }
  | { readonly kind: 'ready'; readonly data: unknown };

export interface AppState {
  readonly route: Route;
  readonly search: string;
  readonly session: Session | null;
  readonly organization: OrganizationRow | null;
  readonly membership: MembershipRow | null;
  readonly grants: readonly ClientGrantRow[];
  /**
   * The people of this organization, by `user_id`.
   *
   * Loaded once per navigation beside the membership read, and passed to
   * `personLabel` by every view rather than reached for from a module-level
   * store: a label is then a function of what this render was given, and an
   * organization switch cannot leave the previous organization's names on
   * screen. Empty until the read lands, which renders the stated fallback
   * rather than a uuid — a name is never invented, and never a stale one.
   */
  readonly directory: PeopleDirectory;
  readonly reviewQueue: readonly api.DraftRow[];
  readonly screen: ScreenState;
  readonly theme: 'light' | 'dark' | null;
  readonly dialog: DialogKind;
  readonly notice: { readonly tone: 'ok' | 'bad'; readonly text: string } | null;
  readonly offline: boolean;
  readonly dirty: boolean;
  /** Reviewer checklist state, held here so a re-render cannot clear a tick. */
  readonly checks: Readonly<Record<string, boolean>>;
  /** The reason typed into the editor or a review note, kept across failures. */
  readonly note: string;
  /**
   * The draft document being edited, held here for the same reason `note` is:
   * `paint()` replaces `innerHTML` on every state change, so a textarea whose
   * value is not in state loses every character as it is typed.
   */
  readonly source: string;
  /** The bundle body fetched for the export dialog. */
  readonly exportBody: string | null;
  /** A one-time invite link. It is not recoverable: only a digest was stored. */
  readonly inviteLink: string | null;
  /**
   * True while a publish or retry request of ours is in flight.
   *
   * NOT the same as "the worker is publishing", which the job row records and
   * the panel reads from `screen.data`. This flag covers the window between a
   * press and the server's answer, and it exists so the control cannot be
   pressed twice into that window — which matters here because the second press
   * would carry a second idempotency key and be refused by the state machine as
   * a duplicate release rather than by anything on screen.
   */
  readonly publishing: boolean;
  /**
   * The reason typed into the lifecycle panel, for the same reason `note` is
   * held: `paint()` replaces `innerHTML` on every state change, so a textarea
   * whose value is not in state loses every character as it is typed.
   *
   * It is dropped on a SUCCESSFUL transition only (`views/lifecycle.ts`), which
   * is the point: a refusal must leave the sentence the person typed exactly
   * where they typed it, and a transition that wrote the reason into the audit
   * trail has nowhere to keep a second copy of it.
   */
  readonly lifecycleReason: string;
}

type DialogKind = 'new-skill' | 'new-toolkit' | 'export' | 'invite' | null;

type Handler = (element: HTMLElement) => void | Promise<void>;

/** The loopback an action handler uses to reach the application. */
export interface AppShell {
  readonly state: AppState;
  readonly client: Client;
  /** The organization slug the current route names. Empty outside one. */
  readonly orgSlug: string;
  navigate(path: string, options?: { readonly replace?: boolean }): void;
  setSearch(search: string): void;
  /**
   * The url change `setSearch` makes, with the revalidation debounced.
   *
   * For a field a person types into. The url and the state still move at once,
   * so the field's own text is never overwritten by a stale query; only the
   * request waits for the typing to stop.
   */
  setSearchDebounced(search: string): void;
  notify(tone: 'ok' | 'bad', text: string): void;
  clearNotice(): void;
  setScreen(screen: ScreenState): void;
  reload(): Promise<void>;
}

export class App implements AppShell {
  readonly client: Client;
  state: AppState;
  private readonly root: HTMLElement;
  private readonly actions: Record<string, Handler> = {};
  private renderQueued = false;
  /**
   * Cancels the pending debounced revalidation, or is null when none is due.
   *
   * A cancel closure rather than a timer handle, so the field holds no platform
   * timer type at all and the field's only job — "stop the load that has not
   * been asked for yet" — is the whole of what it says.
   */
  private cancelPendingSearch: (() => void) | null = null;
  /**
   * The sequence number of the most recently ISSUED load.
   *
   * This is the stale-response guard, and it is a number rather than a boolean
   * because "a load is in flight" is not the question. The question is "is this
   * load still the newest one asked for", which is a comparison. A boolean
   * would have to be cleared by the response that arrives, and the response
   * that arrives is not necessarily the response that was asked for last —
   * that is the whole defect.
   *
   * Measured 2026-10-01, real Chromium, the `q=p` response delayed 2.5s: the
   * table painted "Journey A probe" (the answer to `q=p`) while the field and
   * the url both read `?q=payment`, which the server answers with nothing. The
   * screen disagreed with its own query.
   */
  private loadSequence = 0;
  /**
   * The value last dispatched for each `data-field`, so a repeat is dropped.
   *
   * Keyed by the FIELD NAME rather than the element because the element is
   * replaced on every paint: an identity keyed on the node would miss every
   * repeat, since the node that receives the second event is not the node the
   * first one was dispatched from. Read and written only by `onField`.
   */
  private readonly dispatchedField = new Map<string, string>();
  /**
   * Whether the press under way began on the backdrop rather than on the
   * dialog's own content. Read by `onClick`; written by the `mousedown`
   * listener `start` installs.
   */
  private backdropPress = false;

  constructor(root: HTMLElement) {
    this.root = root;
    this.client = createClient(() => this.state.session?.token ?? null);
    this.state = {
      route: matchRoute(location.pathname),
      search: location.search,
      session: adoptSessionCookie() ?? readStoredSession(),
      organization: null,
      membership: null,
      grants: [],
      directory: new Map<string, string>(),
      reviewQueue: [],
      screen: { kind: 'idle' },
      theme: readStoredTheme(),
      dialog: null,
      notice: null,
      offline: typeof navigator === 'undefined' ? false : !navigator.onLine,
      dirty: false,
      checks: {},
      note: '',
      source: '',
      exportBody: null,
      inviteLink: null,
      publishing: false,
      lifecycleReason: '',
    };
  }

  start(): void {
    applyTheme(this.state.theme);
    registerActions(this);
    this.root.addEventListener('click', (event) => void this.onClick(event));
    // Whether the press that is under way BEGAN on the backdrop. A backdrop
    // click is the third close path a modal owes its opener, and it is the only
    // one no control owns, so it is read here rather than in `views/actions.ts`.
    // It is asked as a separate question because `click` alone cannot answer it:
    // a click's target is the nearest common ancestor of the press and the
    // release, so dragging a selection across the export dialog's `<pre>` and
    // letting go over the backdrop ALSO arrives with the dialog as its target,
    // and closing on it would discard a title being typed. The press is what
    // distinguishes the two.
    this.root.addEventListener('mousedown', (event) => {
      this.backdropPress = event.target instanceof HTMLDialogElement;
    });
    this.root.addEventListener('input', (event) => void this.onField(event));
    this.root.addEventListener('change', (event) => void this.onField(event));
    this.root.addEventListener('submit', (event) => event.preventDefault());
    window.addEventListener('popstate', () => {
      // Same reason as `navigate`: the pending search belongs to the url that was
      // just replaced, and firing it now would ask for the query this history
      // step is undoing.
      this.cancelPendingSearchLoad();
      this.state = {
        ...this.state,
        route: matchRoute(location.pathname),
        search: location.search,
        screen: { kind: 'idle' },
        notice: null,
      };
      void this.load();
      this.render();
    });
    window.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.state.dialog !== null) this.patch({ dialog: null });
    });
    window.addEventListener('offline', () => this.patch({ offline: true }));
    window.addEventListener('online', () => this.patch({ offline: false }));
    document.addEventListener('click', (event) => this.onLink(event));
    void this.load();
    this.render();
  }

  /* ---- state ------------------------------------------------------- */

  patch(change: Partial<AppState>): void {
    this.state = { ...this.state, ...change };
    this.render();
  }

  setScreen(screen: ScreenState): void {
    this.state = { ...this.state, screen };
    this.render();
  }

  notify(tone: 'ok' | 'bad', text: string): void {
    this.state = { ...this.state, notice: { tone, text } };
    this.render();
  }

  clearNotice(): void {
    if (this.state.notice === null) return;
    this.state = { ...this.state, notice: null };
    this.render();
  }

  /**
   * Adopt a session, or drop the one this tab holds.
   *
   * `organization`, `membership`, `grants` and `reviewQueue` are the result of
   * reads issued under one specific token, and they are held for as long as the
   * tab lives. A principal's active organization is a property of that token —
   * `apps/api/src/identity.ts:378` refuses a token without `org_id`, and
   * `authz.current_org()` reads the claim — so replacing the token replaces the
   * organization every one of those rows belongs to. A row kept from the
   * previous one would be painted under the new organization's chrome until the
   * first read came back, which is AUTH-04's "no data carried across by cached
   * queries" failing in the tab. The database enforces the same half on its own
   * side (`0025_current_org_pinning.sql` gates every decision on the claim);
   * nothing here replaces that, and nothing here can widen it — dropping rows is
   * the only direction this method moves.
   *
   * `note`, `source` and `checks` are deliberately NOT cleared. They belong to
   * the unsaved-text recovery (AUTH-03), which is keyed on the user and the
   * draft precisely so that reauthenticating after an expiry can put the work
   * back; `navigate()` clears them on the way to the new screen anyway.
   */
  adoptSession(session: Session | null): void {
    this.state = {
      ...this.state,
      session,
      organization: null,
      membership: null,
      grants: [],
      // Cleared, and this is the load-bearing line of the two. `adoptSession`
      // runs on an organization switch as well as on sign-out, and a directory
      // left over from the previous organization would put that organization's
      // member names under this one's audit trail until the new read lands.
      directory: new Map<string, string>(),
      reviewQueue: [],
      screen: { kind: 'idle' },
    };
  }

  action(name: string, handler: Handler): void {
    this.actions[name] = handler;
  }

  /* ---- routing ----------------------------------------------------- */

  /** The organization slug the route names. Empty outside an organization. */
  get orgSlug(): string {
    return 'org' in this.state.route ? this.state.route.org : '';
  }

  navigate(path: string, options: { readonly replace?: boolean } = {}): void {
    if (this.state.dirty && !globalThis.confirm('You have unsaved text on this screen. Leave anyway?')) {
      return;
    }
    this.state = {
      ...this.state,
      dirty: false,
      notice: null,
      screen: { kind: 'idle' },
      publishing: false,
      lifecycleReason: '',
      checks: {},
      note: '',
      source: '',
      exportBody: null,
      inviteLink: null,
    };
    // A pending debounced search belongs to the screen it was typed on. Leaving
    // it armed would let it fire against a route this navigation has just left,
    // which loads the library behind, say, the admin screen and then paints it
    // there.
    this.cancelPendingSearchLoad();
    if (options.replace === true) history.replaceState(null, '', path);
    else history.pushState(null, '', path);
    this.state = { ...this.state, route: matchRoute(location.pathname), search: location.search };
    void this.load();
    this.render();
  }

  /**
   * Replace the query string in place, and revalidate the screen.
   *
   * Filters live in the url so a view is linkable, but they change on every
   * keystroke, so they must not push a history entry each time or the back
   * button becomes a hundred-step walk through typing.
   */
  setSearch(search: string): void {
    this.commitSearch(search);
    this.cancelPendingSearchLoad();
    void this.load();
  }

  /**
   * The same url change, but the revalidation waits for the typing to stop.
   *
   * The url and the state move IMMEDIATELY here, exactly as in `setSearch`, and
   * only the request waits. That split is the whole design: the value painted
   * into the field comes from `state.search`, so deferring the state as well as
   * the request would let a repaint triggered by anything at all — a notice, a
   * poll, a concurrent load finishing — write the OLD query back over the text
   * being typed. Only the network is allowed to be late.
   *
   * Measured 2026-10-01, real Chromium: 58 requests for the 7 characters of
   * "payment", because each keystroke revalidated the shell as well as the
   * listing. After this, the same 7 characters are one request.
   */
  setSearchDebounced(search: string): void {
    this.commitSearch(search);
    this.cancelPendingSearchLoad();
    // A pending load that is superseded must not be allowed to land: it would
    // resolve into a screen for a query that is no longer the one on screen.
    // Invalidate it here so its response is recognised as stale and dropped.
    this.loadSequence += 1;
    const handle = setTimeout(() => {
      this.cancelPendingSearch = null;
      void this.load();
    }, SEARCH_DEBOUNCE_MS);
    this.cancelPendingSearch = () => {
      clearTimeout(handle);
    };
  }

  /** Move the url and the state, and repaint, without asking the server. */
  private commitSearch(search: string): void {
    history.replaceState(null, '', `${location.pathname}${search}`);
    this.state = { ...this.state, search };
    this.render();
  }

  /** Drop a scheduled revalidation without issuing one. */
  private cancelPendingSearchLoad(): void {
    if (this.cancelPendingSearch === null) return;
    this.cancelPendingSearch();
    this.cancelPendingSearch = null;
  }

  async reload(): Promise<void> {
    await this.load();
    this.render();
  }

  /* ---- data -------------------------------------------------------- */

  async load(): Promise<void> {
    // THE SEQUENCE NUMBER IS TAKEN BEFORE ANY AWAIT, AND IT IS WHAT MAKES A
    // LATE RESPONSE HARMLESS.
    //
    // Two loads are in flight at once whenever the second is issued before the
    // first has resolved — which is every keystroke typed faster than the API
    // answers. Each of those resolves into a screen for a query that is no
    // longer the one on screen. Without this number, whichever response arrives
    // LAST wins, and arrival order has nothing to do with which query was asked
    // for last.
    //
    // Measured 2026-10-01, real Chromium, the `q=p` response delayed by 2.5s:
    // the table painted "Journey A probe" — the answer to `q=p` — while the
    // field and the url both read `?q=payment`, which the server answers with
    // nothing. The screen disagreed with its own query.
    //
    // So a load that is no longer the newest is DROPPED, at every point where it
    // would otherwise write. The check is a comparison rather than a flag
    // because "a load is in flight" is not the question; "is this load still the
    // newest one asked for" is.
    // biome-ignore lint/suspicious/noAssignInExpressions: `sequence` IS the counter after it advances; there is no statement form that yields a post-increment value without reading the field a second time.
    const sequence = (this.loadSequence += 1);
    const route = this.state.route;
    const session = this.state.session;
    // Any poll following a publication ends here, because everything below may
    // replace the screen it was following. `loadScreen` re-arms one after a
    // review load if that draft's publication is still running, so a reload
    // resumes following and a navigation does not.
    stopPublicationPoll();

    // THE LANDING PAGE DECIDES BY WHETHER A SESSION EXISTS, NOT BY WHETHER ONE
    // IS WANTED. `/` is the sign-in, so a person with no session is shown it;
    // and a person who already has one is sent to their organization's library
    // rather than back out to the identity provider, which is what a plain `302
    // /auth/signin` at `/` would do to every one of them on every visit. The
    // navigation is a `replace`, so the landing page is not left behind for the
    // back button to return to.
    if (route.name === 'landing') {
      if (session === null) {
        this.state = { ...this.state, screen: { kind: 'ready', data: null } };
        this.render();
        return;
      }
      if (session.expiresAt !== null && session.expiresAt <= Date.now()) {
        clearStoredSession();
        this.adoptSession(null);
        this.state = { ...this.state, screen: { kind: 'ready', data: null } };
        this.render();
        return;
      }
      const organization = await api.getOrganization(this.client);
      if (organization.ok) {
        // Stored rather than re-fetched by the navigation below, so arriving
        // here costs one read rather than two.
        this.state = { ...this.state, organization: organization.value };
        this.navigate(`/o/${organization.value.slug}/skills`, { replace: true });
        return;
      }
      // The session exists but the organization cannot be read. The landing page
      // is still the honest thing to show — it says what this deployment is and
      // what to do about it — and inventing a redirect to the library here would
      // land on a screen with nothing behind it.
      this.adoptSession(null);
      this.state = { ...this.state, screen: { kind: 'ready', data: null } };
      this.render();
      return;
    }

    if (route.name === 'login' || route.name === 'invite' || route.name === 'callback') {
      this.state = { ...this.state, screen: { kind: 'ready', data: null } };
      this.render();
      return;
    }

    if (session === null) {
      this.navigate(`/login?return=${encodeURIComponent(location.pathname + location.search)}`, {
        replace: true,
      });
      return;
    }

    if (session.expiresAt !== null && session.expiresAt <= Date.now()) {
      clearStoredSession();
      // Everything that organization produced goes with it. The unsaved-text
      // recovery (AUTH-03) reads `source`/`note`, which `adoptSession` leaves
      // alone, so an expiry still costs nothing the person typed.
      this.adoptSession(null);
      this.navigate(`/login?return=${encodeURIComponent(location.pathname + location.search)}`, {
        replace: true,
      });
      return;
    }

    if (this.state.organization === null) {
      const organization = await api.getOrganization(this.client);
      if (!organization.ok) {
        this.setScreen({ kind: 'failed', failure: organization.failure });
        return;
      }
      this.state = { ...this.state, organization: organization.value };
    }

    // The slug in the url must name the organization the token is bound to. A
    // mismatch is answered as not found rather than quietly served.
    const organization = this.state.organization;
    if (organization === null) return;
    if (this.orgSlug !== organization.slug) {
      this.setScreen({ kind: 'failed', failure: { kind: 'not-found', reason: 'not_found' } });
      return;
    }

    // SEQUENCED, NOT CONCURRENT, AND THE ORDER IS THE FIX.
    //
    // `loadAdmin` reads `app.state.membership.role` to decide two different
    // things: whether to FETCH the audit trail at all, and what the screen
    // renders as `isAdmin`. `loadShellData` is what populates that membership.
    // Running the two together is a race, and which side won decided the page.
    //
    // Measured 2026-10-01, real Chromium, signed in as an administrator, six
    // `page.reload()`s of `/o/:org/admin`: the screen rendered its document
    // head as `ROLE ADMIN` — because the head reads `app.state.membership`
    // directly, and that had arrived — while the body rendered the
    // "Read only — you are not an administrator" callout, withheld the audit
    // trail, withheld the self-review control, and withheld V1 section 9's
    // technical diagnostics, because `loadAdmin` had captured `isAdmin: false`
    // microseconds earlier. One screen, contradicting itself, decided by
    // network timing. Six loads produced both answers, so this is not a rare
    // race: it is a race whose winner is not a function of anything a reader did.
    //
    // The cost is one round trip: three shell reads complete before the screen
    // read is issued rather than alongside it. That is paid once per route
    // change and it buys a screen that does not change its mind about who is
    // reading it. The alternative — making `loadAdmin` await a value owned by
    // another loader — would hide the same dependency in a place that does not
    // own it.
    await this.loadShellData(sequence);
    // Checked after the shell read, because that read is the first await in this
    // method and it is the write that decides what `loadAdmin` sees.
    if (sequence !== this.loadSequence) return;
    await this.loadScreen(sequence);
    if (sequence !== this.loadSequence) return;
    this.render();
  }

  /** Rail counters and the account panel, loaded once per route. */
  private async loadShellData(sequence: number): Promise<void> {
    const session = this.state.session;
    if (session === null) return;
    const [membership, queue, grants, people] = await Promise.all([
      api.listMemberships(this.client, session.orgId),
      api.listReviewableDrafts(this.client, session.orgId),
      api.listClientGrants(this.client, session.orgId, session.userId),
      // A FAILED people read KEEPS the directory already held, and that is the
      // whole reason this read is here rather than spread across nine views.
      // Blanking it on a transient failure would replace every name on every
      // screen with "Unknown person" — and on the audit log that reads as "this
      // organization has no history". The directory already held is this same
      // organization's, because `adoptSession` clears it on an organization
      // switch, so keeping it cannot show a neighbour's roster.
      api.listPeople(this.client, session.orgId),
    ]);
    // The write is skipped for a superseded load. Without this, a shell read
    // issued before a keystroke and resolved after it puts the rail counters and
    // the account panel from the older moment on screen — and since this runs
    // before the screen read, it also decides what `loadAdmin` reads for
    // `isAdmin`, so a dropped guard here reopens the admin race documented
    // above rather than merely showing a stale count.
    if (sequence !== this.loadSequence) return;
    this.state = {
      ...this.state,
      membership: membership.ok
        ? (membership.value.find((row) => row.user_id === session.userId) ?? null)
        : null,
      reviewQueue: queue.ok ? queue.value : [],
      grants: grants.ok ? grants.value : [],
      directory: people.ok ? peopleDirectory(people.value) : this.state.directory,
    };
  }

  private async loadScreen(sequence: number): Promise<void> {
    const session = this.state.session;
    const route = this.state.route;
    if (session === null) return;
    const org = session.orgId;
    // A revalidation is not a navigation, and the difference is visible.
    //
    // `setSearch` is how every filter on the library and every tab on the skill
    // detail screen changes, and each of those is a request for the SAME screen
    // with a different query. Replacing that screen with a skeleton meant the
    // toolbar holding `#library-q` left the DOM while it had focus, and a
    // focused element that is removed drops focus to `document.body` — measured
    // 2026-10-01, real Chromium, signed in, continuous typing: the field kept
    // one character and `?q=j` was the longest query the url ever showed. The
    // browser's own focus restoration cannot save it either: `App.paint`
    // captures focus before the innerHTML swap (`app.ts:567`), and by the time
    // it restores, the element it captured is gone — `captureFocus` was handed a
    // `document.body` and had nothing to hand back.
    //
    // So the ready screen is KEPT while a screen that already has data is
    // revalidated, and the skeleton is shown only when there is nothing to
    // keep. That is not a softening of V1 section 9's loading state: every
    // route change resets `screen` to `{kind:'idle'}` first — `navigate` at
    // `app.ts:302`, the `popstate` handler at `app.ts:211`, `adoptSession` at
    // `app.ts:279` — so a real navigation still paints the skeleton, and a
    // first load of a screen this tab has never seen still does too. A reload
    // from a ready screen keeps that screen for the same reason, and lands on
    // the failure panel if the request fails, which is the honest outcome.
    if (this.state.screen.kind !== 'ready') this.setScreen({ kind: 'loading' });

    let result: Result<unknown>;
    switch (route.name) {
      case 'skills':
        result = await loadLibrary(this, org);
        break;
      case 'skill':
        result = await loadSkillDetail(this, org, route.id);
        break;
      case 'skill-edit': {
        const editor = await loadSkillEditor(this, org, route.id);
        // Before `setScreen`, so the recovered text is in the state the first
        // paint reads rather than arriving one frame later.
        if (editor.ok) this.recoverUnsaved(editor.value);
        // And then the stored bytes, for a tab that kept nothing to put back.
        // `recoverUnsaved` runs first and wins: text this person was writing
        // must not be replaced by what the draft held a moment ago. Adopting
        // into the same state field is what stops the next paint from
        // rendering an empty textarea over a draft that has a document.
        if (editor.ok && this.state.source === '') {
          this.state = { ...this.state, source: editor.value.draftSource ?? '' };
        }
        result = editor;
        break;
      }
      case 'review':
        result = await loadReview(this, org, route.draftId);
        break;
      case 'toolkits':
        result = await loadToolkits(this, org);
        break;
      case 'improvements':
        result = await loadImprovements(this, org);
        break;
      case 'connect':
        result = await loadConnect(this, org);
        break;
      case 'authorize':
        result = await loadAuthorize(this, org);
        break;
      case 'admin':
        result = await loadAdmin(this, org);
        break;
      default:
        result = { ok: true, value: null };
    }
    // The write that matters. Everything above this line computed a screen for
    // the query this load asked about; if a newer load has been issued since,
    // that screen describes a query the person has already moved past, and
    // painting it is how the table came to answer `q=p` while the url read
    // `q=payment`.
    //
    // Dropped silently rather than as a failure panel: the request was not
    // refused, it was superseded, and the newest load will paint its own answer
    // when it resolves. A person typing should not be told anything about it.
    if (sequence !== this.loadSequence) return;
    this.setScreen(
      result.ok
        ? { kind: 'ready', data: result.value }
        : { kind: 'failed', failure: result.failure },
    );
    // After `setScreen`, because that is the first moment this draft's data is
    // in state and the poll refuses to touch a screen it cannot recognise. One
    // trigger for every route in, rather than one in the Publish handler: a tab
    // that reloads mid-publication has to resume following, and only the load
    // knows that a job is still running.
    if (route.name === 'review') startPublicationPoll(this, route.draftId);
  }

  /**
   * Put back the text this tab kept for the draft the editor just resolved to.
   *
   * Runs before the first paint of the screen so neither box is ever briefly
   * empty, and it restores each field only when that field is empty. A reload
   * that races a keystroke must not replace what is being typed with what was
   * stored a moment ago, and the two fields are restored independently for the
   * same reason: an empty reason must not suppress a recovered document, and a
   * recovered document must not imply a recovered reason.
   *
   * The notice says the text was recovered and not saved, because it was not
   * saved. Rendering a successful save here would be the exact failure section
   * 9 forbids, produced by the feature that exists to prevent losing work.
   */
  private recoverUnsaved(data: EditorData): void {
    const session = this.state.session;
    const draft = data.active;
    if (session === null || draft === null) return;
    const recovered = readUnsaved(session.userId, draft.id);
    if (recovered === null) return;
    const note = this.state.note === '' ? recovered.note : this.state.note;
    const source = this.state.source === '' ? recovered.source : this.state.source;
    if (note === this.state.note && source === this.state.source) return;
    if (note === '' && source === '') return;
    this.state = {
      ...this.state,
      note,
      source,
      dirty: true,
      notice: {
        tone: 'ok',
        text:
          'Unsaved text from earlier in this tab was put back. It is still unsaved — ' +
          'nothing is stored until Save draft reports a version and the document reads back.',
      },
    };
  }

  /* ---- rendering --------------------------------------------------- */

  render(): void {
    if (this.renderQueued) return;
    this.renderQueued = true;
    queueMicrotask(() => {
      this.renderQueued = false;
      this.paint();
    });
  }

  private paint(): void {
    // A PAINT HAS TWO HALVES, AND ONLY ONE OF THEM NEEDS A DOM.
    //
    //   Building the HTML needs nothing but the state. Reconciling the
    //   browser's focus and dialog state with it needs `document`,
    //   `HTMLElement`, `HTMLDialogElement` and `CSS` — browser globals that
    //   the unit tests, which render the application without a browser, do
    //   not have. Naming one of them is a ReferenceError, and it is raised
    //   inside the QUEUED MICROTASK `render()` schedules, so it does not
    //   fail the thing that caused it: it surfaces later as an unhandled
    //   error between tests, and the suite goes red for a reason that has
    //   nothing to do with the thing under test.
    //
    //   Measured 2026-10-20 in CI: "ReferenceError: document is not
    //   defined" and then "ReferenceError: HTMLDialogElement is not
    //   defined", each with six failures behind it.
    //
    //   So the question is asked ONCE, here, where the two halves meet.
    const html = this.html().value;
    if (
      typeof document === 'undefined' ||
      typeof HTMLElement === 'undefined' ||
      typeof HTMLDialogElement === 'undefined' ||
      typeof CSS === 'undefined'
    ) {
      this.root.innerHTML = html;
      return;
    }
    const focused = this.captureFocus();
    const scrolls = captureScroll(this.root);
    this.root.innerHTML = html;
    restoreScroll(this.root, scrolls);
    this.restoreFocus(focused);
    this.syncDialog();
  }

  /**
   * Keep the one dialog open across a re-render, and put focus back when it
   * closes.
   *
   * The element is replaced on every paint, so `showModal()` has to be called
   * again each time; and because the control that opened it is gone, the
   * browser cannot restore focus for us. The opening handler records the
   * control's id and it is honoured here.
   *
   * THE CLOSING PAINT HAS NO DIALOG ELEMENT IN IT, AND THAT IS THE POINT
   *   `renderDialog` answers `raw('')` as soon as `state.dialog` is null
   *   (`views/dialog.ts:22`), so by the time this runs the element has already
   *   been removed by `this.root.innerHTML = …` on the line above. The previous
   *   shape began with `if (!(element instanceof HTMLDialogElement)) return;`,
   *   so on EVERY close it returned there and the return-focus code below it
   *   never ran at all — measured 2026-10-01, real Chromium, Escape on the
   *   export dialog leaves `document.activeElement` as `document.body`. It was
   *   not `restoreFocus` failing: it is handed `null` because by then the focus
   *   has already fallen off the removed dialog.
   *
   *   So the close is handled from `state`, which is the only thing that knows
   *   whether the dialog is open, and the element is consulted only for the two
   *   things an element can do: re-open it while `state` says it should be open,
   *   and run `close()` on it if one survived the paint with `open === true`.
   *
   *   The recorded id is consumed EXACTLY ONCE per close, whether or not the
   *   element that opened the dialog still exists. Two reasons. First, a close
   *   that cannot find its target must not leave the id behind: it would be
   *   applied to whatever dialog opened next, which is focus put somewhere the
   *   person was never. Second, `consumeDialogReturnFocus` clears before it
   *   returns, so the second close of the same dialog has nothing to consume
   *   and cannot move focus twice.
   */
  private syncDialog(): void {
    const element = this.root.querySelector('dialog[data-dialog]');
    const dialog = element instanceof HTMLDialogElement ? element : null;
    if (this.state.dialog !== null) {
      if (dialog !== null && !dialog.open) dialog.showModal();
      return;
    }
    if (dialog !== null && dialog.open) dialog.close();
    const target = consumeDialogReturnFocus();
    if (target === null) return;
    const returnTo = this.root.querySelector(`#${CSS.escape(target)}`);
    if (returnTo instanceof HTMLElement) returnTo.focus();
  }

  html(): RawHtml {
    const route = this.state.route;
    if (
      route.name === 'landing' ||
      route.name === 'login' ||
      route.name === 'invite' ||
      route.name === 'callback'
    ) {
      return renderGate(this, route);
    }
    if (this.state.session === null || this.state.organization === null) {
      return renderGate(this, route);
    }
    return renderShell(this, this.screenHtml());
  }

  private screenHtml(): RawHtml {
    const route = this.state.route;
    const screen = this.state.screen;
    switch (route.name) {
      case 'skills':
        return renderLibrary(this, route.org, screen);
      case 'skill':
        return renderSkillDetail(this, screen);
      case 'skill-edit':
        return renderSkillEditor(this, screen);
      case 'review':
        return renderReview(this, screen);
      case 'toolkits':
        return renderToolkits(this, screen);
      case 'improvements':
        return renderImprovements(this, screen);
      case 'connect':
        return renderConnect(this, screen);
      case 'authorize':
        return renderAuthorize(this, screen);
      case 'admin':
        return renderAdmin(this, screen);
      case 'not-found':
        return html`
          <h1>No such page</h1>
          <p class="lede">${route.path} is not one of the screens this application serves.</p>
        `;
      default:
        return raw('');
    }
  }

  /* ---- events ------------------------------------------------------ */

  private onLink(event: MouseEvent): void {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey) return;
    const anchor = (event.target as HTMLElement | null)?.closest('a');
    if (anchor === null || anchor === undefined) return;
    const href = anchor.getAttribute('href');
    if (href === null || href.startsWith('http') || href.startsWith('#')) return;
    // `data-full-page` is the opt-out, and it is needed rather than decorative.
    // The sign-in control is a same-origin link to `/auth/signin`, which this
    // process answers with a `302` to the identity provider. Intercepted as an
    // ordinary navigation it would never leave the browser: `matchRoute` has no
    // route for it, so the router would render "No such page" and the person
    // would be told to go back to sign in, from the sign-in screen.
    if (anchor.dataset.fullPage !== undefined) return;
    event.preventDefault();
    this.navigate(href);
  }

  /**
   * The single resolution rule for `data-action` and `data-field` lives in
   * `lib/actions.ts` and is asserted in `test/actions.test.ts`. It is a
   * longest-prefix match rather than an exact match because an action name
   * carries its parameters, and it was an exact match, which silently made
   * revoking a client grant, removing a member and all three proposal
   * decisions into controls that did nothing at all while still looking live.
   */

  private async onClick(event: MouseEvent): Promise<void> {
    /**
     * A click on the backdrop closes the dialog, and returns focus with it.
     *
     * Chromium delivers a backdrop click with the `<dialog>` element itself as
     * the target, so `onClick` is the place it arrives at — measured 2026-10-01,
     * real Chromium, 1440px and 390px, toolkits dialog open with its box at
     * `x 423 y 290 w 594 h 320`: a real mouse click at (20, 20) left
     * `dialog.open === true` and put focus on the dialog, because nothing
     * listened for it. Native `<dialog>` closes on Escape and nothing else.
     *
     * It closes through `patch({ dialog: null })` and not by calling
     * `dialog.close()`, because the focus return is driven from state: that is
     * the route Escape and the Close button already take, so all three paths
     * land on the same code and cannot drift apart.
     */
    if (this.backdropPress && this.state.dialog !== null) {
      if (event.target instanceof HTMLDialogElement) {
        this.backdropPress = false;
        this.patch({ dialog: null });
        return;
      }
    }
    const element = (event.target as HTMLElement | null)?.closest('[data-action]');
    if (!(element instanceof HTMLElement)) return;
    const name = element.dataset.action;
    if (name === undefined) return;
    const handler = resolveAction(this.actions, name);
    if (handler === undefined) return;
    await handler(element);
  }

  private onField(event: Event): void {
    const element = event.target;
    if (
      !(element instanceof HTMLInputElement) &&
      !(element instanceof HTMLTextAreaElement) &&
      !(element instanceof HTMLSelectElement)
    ) {
      return;
    }
    const name = element.dataset.field;
    if (name === undefined) return;
    // ONE FIELD, ONE DISPATCH, EVEN WHEN THE BROWSER SAYS IT TWICE.
    //
    // `input` and `change` are both wired here, because a `<select>` only ever
    // fires `change` and a text field only ever needs `input`. On the library
    // search they arrive as a PAIR for a single keystroke, and the reason is
    // this class's own repaint: `paint` assigns `root.innerHTML`, which retires
    // the focused input, and removing a modified input fires `change` at it. So
    // every keystroke dispatched its handler twice, and each dispatch is a full
    // revalidation — measured 2026-10-01, real Chromium: 14 `/v1/skills` reads
    // for 7 keystrokes, two per character, with identical query strings.
    //
    // The rule is on the VALUE, not on the event type, so it cannot suppress a
    // real second change: a select fires only `change` and is unaffected, and a
    // text field whose value genuinely changed always differs from the last one
    // dispatched for that field. What it suppresses is precisely the repeat of
    // a value this tab has already asked about.
    const dispatched = this.dispatchedField.get(name);
    const current = 'value' in element ? String(element.value) : '';
    if (dispatched !== undefined && dispatched === current) return;
    this.dispatchedField.set(name, current);
    // The same prefix rule as `data-action`: a field may name its record
    // (`review.check.instructions`, `improvements.note.<proposalId>`), so an
    // exact lookup missed every parameterised one.
    const handler = resolveAction(this.actions, `field:${name}`);
    if (handler === undefined) return;
    void handler(element);
  }

  private captureFocus(): { id: string; start: number | null; end: number | null } | null {
    const element = document.activeElement;
    if (!(element instanceof HTMLElement) || element.id === '') return null;
    const ranged =
      element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement;
    return {
      id: element.id,
      start: ranged ? element.selectionStart : null,
      end: ranged ? element.selectionEnd : null,
    };
  }

  private restoreFocus(saved: { id: string; start: number | null; end: number | null } | null): void {
    if (saved === null) return;
    const element = this.root.querySelector(`#${CSS.escape(saved.id)}`);
    if (!(element instanceof HTMLElement)) return;
    element.focus({ preventScroll: true });
    if (
      (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) &&
      saved.start !== null &&
      saved.end !== null
    ) {
      element.setSelectionRange(saved.start, saved.end);
    }
  }
}

function captureScroll(root: HTMLElement): Record<string, number> {
  const positions: Record<string, number> = {};
  for (const element of root.querySelectorAll<HTMLElement>('[data-scroll]')) {
    const key = element.dataset.scroll;
    if (key !== undefined) positions[key] = element.scrollTop;
  }
  return positions;
}

function restoreScroll(root: HTMLElement, positions: Record<string, number>): void {
  for (const element of root.querySelectorAll<HTMLElement>('[data-scroll]')) {
    const key = element.dataset.scroll;
    const top = key === undefined ? undefined : positions[key];
    if (top !== undefined) element.scrollTop = top;
  }
}

/* ---- session and theme storage -------------------------------------- */

function readStoredSession(): Session | null {
  const raw = sessionStorage.getItem(SESSION_KEY);
  if (raw === null) return null;
  const session = decodeSession(raw);
  if (session === null) sessionStorage.removeItem(SESSION_KEY);
  return session;
}

/**
 * Take the session token the server left for this tab, and make it a tab value.
 *
 * The web process mints the session and cannot put it in the URL — `oidc.ts`
 * refuses that for a subject, and a bearer token in a URL is worse than a
 * subject in one because it IS the credential. So it arrives in a cookie, and
 * this is where that cookie stops being a cookie.
 *
 * THE COOKIE IS DELETED FIRST, THEN READ. A tab that reads the token, moves it
 * to `sessionStorage`, and then dies before clearing the cookie would leave a
 * credential on disk for a minute; clearing before reading cannot, because a
 * cleared cookie is gone whether or not this function goes on to throw.
 *
 * `storeSession` still applies — it is `decodeSession`, which refuses a token
 * that does not name a subject and an organization. That is a shape check,
 * not a trust decision: `apps/api` re-verifies every token on every request,
 * and nothing here can make an invalid one work.
 */
function adoptSessionCookie(): Session | null {
  const match = /(?:^|;\s*)skills-manager\.session=([^;]*)/.exec(document.cookie);
  if (match === null) return null;
  document.cookie = 'skills-manager.session=; Path=/; SameSite=Lax; Max-Age=0';
  const token = decodeURIComponent(match[1] ?? '');
  return token === '' ? null : storeSession(token);
}

export function storeSession(token: string): Session | null {
  const session = decodeSession(token);
  if (session !== null) sessionStorage.setItem(SESSION_KEY, token);
  return session;
}

export function clearStoredSession(): void {
  sessionStorage.removeItem(SESSION_KEY);
}

function readStoredTheme(): 'light' | 'dark' | null {
  const raw = localStorage.getItem(THEME_KEY);
  return raw === 'light' || raw === 'dark' ? raw : null;
}

function applyTheme(theme: 'light' | 'dark' | null): void {
  if (theme === null) document.documentElement.removeAttribute('data-theme');
  else document.documentElement.setAttribute('data-theme', theme);
}

export function storeTheme(theme: 'light' | 'dark' | null): void {
  if (theme === null) localStorage.removeItem(THEME_KEY);
  else localStorage.setItem(THEME_KEY, theme);
  applyTheme(theme);
}

